import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, HostListener, ViewChild, inject } from '@angular/core';
import { ContextMenu } from 'primeng/contextmenu';
import { firstValueFrom } from 'rxjs';
import { Config } from 'src/modules/app-template/models/config.model';
import { LoginUserDatabase } from 'src/modules/database/shared/login-user-database.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { UserInteractionItem } from 'src/modules/utils/shared/user-interaction-item.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { MainAppService } from '../../services/main-app.service';

@Component({
    selector: 'app-main',
    styleUrls: ['./app-main.component.scss'],
    templateUrl: './app-main.component.html'
})
export class AppMainComponent implements AfterViewInit {

    static instance: AppMainComponent;

    app = inject(MainAppService);
    http = inject(HttpClient);

    inited = false;
    activeTabIndex: number;
    sidebarActive: boolean;
    invalidBrowser = false;
    privacyPolicyAccepted = false;
    privacyPolicy = false;
    privacyPolicyHtml: any;

    @ViewChild("cm") cm: ContextMenu;

    constructor() {
        AppMainComponent.instance = this;
        this.invalidBrowser = this.app.deviceDetector.browser === "internetexplorer";
        this.privacyPolicy = Config.get().privacyPolicy;
    }

    ngDoCheck(): void {
        this.app.changeDetectionCount++;
    }

    onHotkeyDeveloper(): void {
        let nodes = document.querySelectorAll(":hover");
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < nodes.length; i++) {
            let p = (window as any).ng.getComponent(nodes[i]);
            if (p != null) {
                console.log(p);
            }
        }
    }

    onHotkeySidebar(): void {
        let model = [
            {
                label: "Sidebar ein-/ausblenden",
                command: () => {
                    this.app.showSidebar = !this.app.showSidebar;
                }
            },
            {
                label: "Angular-Routen debuggen",
                command: () => {
                    this.app.debugRoutes = !this.app.debugRoutes;
                }
            },
            {
                label: "Interaktions-Historie auf Konsole ausgeben",
                command: () => {
                    console.log(Utils.arrayReverse(Utils.arraySort(this.app.userInteractionHistory)).map(item => item.toString()));
                }
            },
            {
                label: "Fehler werfen",
                command: () => {
                    throw new Error();
                }
            },
            {
                label: "Erweitere Dev-Optionen freischalten/abschalten",
                command: () => {
                    this.app.advancedDeveloper = !this.app.advancedDeveloper;
                }
            },
            {
                label: "Angular ChangeDetection Debugging",
                command: () => {
                    let oldConsole = window.console.log;
                    window.console.log = (data: any[]) => {
                        oldConsole(data);
                        if (Utils.isString(data)) {
                            data = [data];
                        }
                        if (data.length > 0 && Utils.isString(data[0]) && (data[0] as string).endsWith("ms per check")) {
                            this.app.showToast("info", "Change Detection Analyse", data[0] as string);
                        }
                    };
                    Utils.setTimerOnce(1000, () => {
                        window.console.log = oldConsole;
                    });
                    (window as any).ng.profiler.timeChangeDetection();
                }
            },
            {
                label: "Angular ChangeDetection Debugging Periodic",
                command: () => {
                   Utils.setTimer(1000, () => console.log("Anzahl ChangeDetections: " + this.app.changeDetectionCount));
                }
            }


        ];
        this.app.openContextMenu(model);
    }

    onHotkeyOnSelection(): void {
        if (this.app.onSelectionCallback != null) {
            this.app.onSelectionCallback();
        }
    }

    @HostListener('document:click', ['$event'])
    async onClick(event: Event): Promise<void> {
        if (event.target != null) {
            let debugString = event.target instanceof HTMLElement ? GuiUtils.getHtmlElementDebugString(event.target as HTMLElement) : [Utils.toString(event.target)];
            this.app.userInteractionHistory.push(new UserInteractionItem(this.lastMouseDown ?? new Date(), "CLICK", debugString));
            this.lastMouseDown = null;
        }
        if (event.target instanceof HTMLAnchorElement && event.target.className === 'dynamic-router-link') {
            event.preventDefault();
            const route = event.target?.getAttribute('href');
            if (route) {
                await this.app.navigateTo([`/${route}`]);
            }
        }
    }

    lastMouseDown: Date;

    @HostListener('document:mousedown', ['$event'])
    public onMouseDown(event: Event): boolean {
        this.app.userInteracted();

        if (event.target != null) {
            this.lastMouseDown = new Date();
        }
        return true;
     }

    @HostListener('document:mousemove', ['$event'])
    public onMouseMove(event: Event): boolean {
        this.app.mouseX = (event as any).pageX;
        this.app.mouseY = (event as any).pageY;
        return true;
     }

     @HostListener('window:keydown', ['$event'])
     public onKeyPress(event: KeyboardEvent): boolean {
        let key = this.app.userKeyPress(event);
        if (key == "SHIFT-ALT-Y") {
            this.onHotkeyDeveloper();
            event.stopPropagation();
            event.preventDefault();
        }
        else if (key == "SHIFT-ALT-S") {
            this.onHotkeySidebar();
            event.stopPropagation();
            event.preventDefault();
        }
        else if (key == "SHIFT-ALT-C") {
            this.onHotkeyOnSelection();
            event.stopPropagation();
            event.preventDefault();
        }
        return true;
     }

     @HostListener('document:wheel', ['$event'])
     public onWheel(_: Event): boolean {
        this.app.userInteracted();
        return true;
     }

     @HostListener('document:touchstart', ['$event'])
     public onTouchStart(_: Event): boolean {
        this.app.userInteracted();
        return true;
     }

     @HostListener('document:scroll', ['$event'])
     public onScroll(_: Event): boolean {
        this.app.userInteracted();
        return true;
     }

    async ngAfterViewInit(): Promise<void> {
        if (this.invalidBrowser) {
            return;
        }

        if (this.privacyPolicy) {
            this.privacyPolicyAccepted = this.app.auth.userDetails?.getDatabaseUser()?.privacyPolicyAccepted;
            this.privacyPolicyHtml = this.privacyPolicyAccepted ? "" : await firstValueFrom(this.http.get("assets/portal/templates/privacyPolicy.html", { responseType: "text"}));
        }

        await this.app.featureModules.beforeAppStart();

        GuiUtils.angularTimer(() => {
            this.inited = true;
        });
    }

    async onTabClick(event, index: number): Promise<void> {
        if (this.app.visibleMenuTabs[index].routerLink != null) {
            this.activeTabIndex = index;
            this.sidebarActive = false;
            await this.app.navigateTo(this.app.visibleMenuTabs[index].routerLink);
        }
        else if (this.activeTabIndex === index) {
            this.sidebarActive = !this.sidebarActive;
        }
        else {
            this.activeTabIndex = index;
            this.sidebarActive = true;
        }

        event.preventDefault();
    }

    closeSidebar(event: Event): void {
        this.sidebarActive = false;
        event.preventDefault();
    }

    onTopbarSubItemClick(event: Event): void {
        event.preventDefault();
    }

    isDesktop(): boolean {
        return window.innerWidth > 1024;
    }

    async acceptPrivacyPolicy(): Promise<void> {
        let user = this.app.auth.userDetails?.getDatabaseUser();
        if (user != null) {
            user.privacyPolicyAccepted = true;
            await RestEndpoint.main().post().body(user).run("api/app/databaseUser/acceptPrivacyPolicy").getText();
        }
        this.privacyPolicyAccepted = true;
    }

    async declinePrivacyPolicy(): Promise<void> {
        let user = this.app.auth.userDetails?.getDatabaseUser();
        if (user != null) {
            await RestEndpoint.main().post().body(user).run("api/app/databaseUser/declinePrivacyPolicy").getText();
        }
        await this.app.auth.logout();
    }


}
